import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { ConfirmDialog } from '../components/admin';
import { Plus, Trash2, Save, Edit3, X, Check, MessageSquare, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: 'meslek', tr: 'Meslek', en: 'Profession' },
  { id: 'sehir', tr: 'Şehir', en: 'City' },
  { id: 'reklam_turu', tr: 'Reklam Türü', en: 'Ad Source' },
  { id: 'reklam_firmasi', tr: 'Reklam Firması', en: 'Ad Company' },
  { id: 'paket', tr: 'Paket', en: 'Package' },
  { id: 'belge_turu', tr: 'Belge Türü', en: 'Document Type' },
  { id: 'sms', tr: 'SMS Kalıpları', en: 'SMS Templates' },
];

const INPUT_CLS =
  'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none transition-colors';

// ── SMS Kalıpları Tab ─────────────────────────────────────────────────────────
function SmsKaliplariTab() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [kaliplar, setKaliplar] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(false);

  // Yeni kalıp form
  const [newAd, setNewAd] = useState('');
  const [newMesaj, setNewMesaj] = useState('');
  const [saving, setSaving] = useState(false);

  // Düzenleme
  const [editId, setEditId] = useState(null);
  const [editAd, setEditAd] = useState('');
  const [editMesaj, setEditMesaj] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Silme confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchKaliplar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/sms-kaliplari`, { headers: authHeaders() });
      setKaliplar(r.data.kaliplar || []);
      setPlaceholders(r.data.desteklenen_placeholderlar || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchKaliplar(); }, []);

  // Placeholder chip → textarea'ya ekle (yeni form)
  const insertToNew = (ph) => setNewMesaj((m) => m + ph);
  // Placeholder chip → düzenleme textarea'ya ekle
  const insertToEdit = (ph) => setEditMesaj((m) => m + ph);

  const handleAdd = async () => {
    if (!newAd.trim() || !newMesaj.trim()) {
      toast.error(tr('Ad ve mesaj zorunludur.', 'Name and message are required.'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/sms-kaliplari`,
        { ad: newAd.trim(), mesaj: newMesaj.trim(), aktif: true },
        { headers: authHeaders() }
      );
      setNewAd('');
      setNewMesaj('');
      fetchKaliplar();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (k) => {
    setEditId(k.kalip_id);
    setEditAd(k.ad);
    setEditMesaj(k.mesaj);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditAd('');
    setEditMesaj('');
  };

  const handleSaveEdit = async (id) => {
    if (!editAd.trim() || !editMesaj.trim()) return;
    setEditSaving(true);
    try {
      await axios.put(
        `${API}/admin/sms-kaliplari/${id}`,
        { ad: editAd.trim(), mesaj: editMesaj.trim() },
        { headers: authHeaders() }
      );
      cancelEdit();
      fetchKaliplar();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setEditSaving(false);
    }
  };

  const toggleAktif = async (k) => {
    try {
      await axios.put(
        `${API}/admin/sms-kaliplari/${k.kalip_id}`,
        { aktif: !k.aktif },
        { headers: authHeaders() }
      );
      fetchKaliplar();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/sms-kaliplari/${deleteTarget}`, { headers: authHeaders() });
      setDeleteTarget(null);
      fetchKaliplar();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Yeni kalıp formu */}
      <div className="card-clay p-5 space-y-3">
        <h3 className="text-sm font-bold text-[var(--text-main)] flex items-center gap-2">
          <Plus size={15} className="text-[var(--brand-primary)]" />
          {tr('Yeni Kalıp Ekle', 'Add New Template')}
        </h3>
        <div>
          <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide">
            {tr('Kalıp Adı', 'Template Name')}
          </label>
          <input
            value={newAd}
            onChange={(e) => setNewAd(e.target.value)}
            placeholder={tr('Örn: Hoşgeldiniz SMS', 'e.g.: Welcome SMS')}
            className={INPUT_CLS}
            data-testid="sms-kalip-new-ad"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block uppercase tracking-wide">
            {tr('Mesaj', 'Message')}
          </label>
          <textarea
            rows={4}
            value={newMesaj}
            onChange={(e) => setNewMesaj(e.target.value)}
            placeholder={tr('Mesaj içeriğini girin. {adSoyad} gibi placeholder\'lar kullanabilirsiniz.', 'Enter message content. You can use placeholders like {adSoyad}.')}
            className={INPUT_CLS + ' resize-none'}
            data-testid="sms-kalip-new-mesaj"
          />
          {placeholders.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] text-[var(--text-muted)] font-semibold mb-1">
                {tr('Tıkla-ekle placeholder\'lar:', 'Click-to-insert placeholders:')}
              </p>
              <div className="flex flex-wrap gap-1">
                {placeholders.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    onClick={() => insertToNew(ph)}
                    className="px-2 py-0.5 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-700 text-[11px] font-mono font-semibold hover:bg-cyan-100 transition-colors"
                  >
                    {ph}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleAdd}
            disabled={saving || !newAd.trim() || !newMesaj.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-bold border-2 border-green-600 border-b-4 border-b-green-700 hover:bg-green-600 disabled:opacity-50"
            data-testid="sms-kalip-add-btn"
          >
            <Plus size={15} />
            {saving ? tr('Kaydediliyor...', 'Saving...') : tr('Kalıp Ekle', 'Add Template')}
          </button>
        </div>
      </div>

      {/* Kalıp listesi */}
      <div className="card-clay overflow-hidden">
        {loading && (
          <div className="p-4 text-center text-sm text-[var(--text-muted)]">
            {tr('Yükleniyor...', 'Loading...')}
          </div>
        )}
        {!loading && kaliplar.length === 0 && (
          <div className="p-6 text-center text-sm text-[var(--text-muted)]">
            {tr('Henüz kalıp yok. Yukarıdan ekleyin.', 'No templates yet. Add one above.')}
          </div>
        )}
        {!loading && kaliplar.map((k) => (
          <div
            key={k.kalip_id}
            className="border-t border-[var(--border-light)] first:border-t-0"
            data-testid={`sms-kalip-row-${k.kalip_id}`}
          >
            {editId === k.kalip_id ? (
              /* Düzenleme satırı */
              <div className="p-4 space-y-3">
                <input
                  value={editAd}
                  onChange={(e) => setEditAd(e.target.value)}
                  className={INPUT_CLS}
                  autoFocus
                />
                <textarea
                  rows={4}
                  value={editMesaj}
                  onChange={(e) => setEditMesaj(e.target.value)}
                  className={INPUT_CLS + ' resize-none'}
                />
                {placeholders.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {placeholders.map((ph) => (
                      <button
                        key={ph}
                        type="button"
                        onClick={() => insertToEdit(ph)}
                        className="px-2 py-0.5 rounded-lg bg-cyan-50 border border-cyan-200 text-cyan-700 text-[11px] font-mono font-semibold hover:bg-cyan-100"
                      >
                        {ph}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1.5 rounded-xl border-2 border-[var(--border-light)] text-xs font-bold hover:bg-gray-50"
                  >
                    {tr('İptal', 'Cancel')}
                  </button>
                  <button
                    onClick={() => handleSaveEdit(k.kalip_id)}
                    disabled={editSaving || !editAd.trim() || !editMesaj.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[var(--brand-primary)] text-white text-xs font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                  >
                    {editSaving ? <Save size={13} className="animate-pulse" /> : <Check size={13} />}
                    {tr('Kaydet', 'Save')}
                  </button>
                </div>
              </div>
            ) : (
              /* Normal satır */
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-bold text-sm text-[var(--text-main)]">{k.ad}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      k.aktif !== false
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}>
                      {k.aktif !== false ? tr('Aktif', 'Active') : tr('Pasif', 'Inactive')}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] leading-relaxed line-clamp-2">
                    {k.mesaj?.slice(0, 80)}{k.mesaj?.length > 80 ? '…' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Aktif toggle */}
                  <button
                    onClick={() => toggleAktif(k)}
                    title={k.aktif !== false ? tr('Pasife al', 'Deactivate') : tr('Aktife al', 'Activate')}
                    className={`p-1.5 rounded-lg ${
                      k.aktif !== false
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-50'
                    }`}
                  >
                    {k.aktif !== false ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  </button>
                  {/* Düzenle */}
                  <button
                    onClick={() => startEdit(k)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Edit3 size={15} />
                  </button>
                  {/* Sil */}
                  <button
                    onClick={() => setDeleteTarget(k.kalip_id)}
                    className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Silme onayı */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v && !deleting) setDeleteTarget(null); }}
        title={tr('Kalıbı Sil', 'Delete Template')}
        description={tr(
          'Bu SMS kalıbı kalıcı olarak silinecek. Bu işlem geri alınamaz.',
          'This SMS template will be permanently deleted. This action cannot be undone.'
        )}
        variant="danger"
        confirmLabel={tr('Sil', 'Delete')}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function AdminDefinitions() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  // URL'den aktif tab başlangıç değeri
  const initialTab = searchParams.get('tab') || 'meslek';
  const [active, setActive] = useState(initialTab);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newMeb, setNewMeb] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  // Silme confirm
  const [deleteDefTarget, setDeleteDefTarget] = useState(null);
  const [deletingDef, setDeletingDef] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/definitions?type=${active}`, { headers: authHeaders() });
      setItems(r.data.definitions || []);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  };

  const changeTab = (id) => {
    setActive(id);
    setSearchParams(id !== 'meslek' ? { tab: id } : {});
  };

  useEffect(() => {
    if (active !== 'sms') {
      fetchItems();
      setNewLabel('');
      setNewMeb(false);
      setEditId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const addItem = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const body = { type: active, label_tr: newLabel.trim() };
      if (active === 'belge_turu') body.meb_kritik = newMeb;
      await axios.post(`${API}/admin/definitions`, body, { headers: authHeaders() });
      setNewLabel(''); setNewMeb(false);
      fetchItems();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(false); }
  };

  const saveEdit = async (id) => {
    if (!editLabel.trim()) return;
    try {
      await axios.put(`${API}/admin/definitions/${id}`, { label_tr: editLabel.trim(), label_en: editLabel.trim() }, { headers: authHeaders() });
      setEditId(null);
      fetchItems();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const toggleMeb = async (it) => {
    try {
      await axios.put(`${API}/admin/definitions/${it.def_id}`, { meb_kritik: !it.meb_kritik }, { headers: authHeaders() });
      fetchItems();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const handleDeleteDef = async () => {
    if (!deleteDefTarget) return;
    setDeletingDef(true);
    try {
      await axios.delete(`${API}/admin/definitions/${deleteDefTarget}`, { headers: authHeaders() });
      setDeleteDefTarget(null);
      fetchItems();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setDeletingDef(false); }
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: tr('Tanımlar', 'Definitions') }]}
        title={tr('Tanımlar', 'Definitions')}
        subtitle={tr('Meslek, şehir, paket, belge türü, SMS kalıpları gibi sözlükleri yönetin', 'Manage lookups like profession, city, package, document type, SMS templates')}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {TYPES.map((t) => (
          <button key={t.id} onClick={() => changeTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border-2 transition-colors ${active === t.id ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]' : 'bg-white border-[var(--border-light)] text-[var(--text-main)] hover:bg-gray-50'}`}
            data-testid={`def-tab-${t.id}`}>
            {t.id === 'sms' && <MessageSquare size={14} />}
            {lang === 'tr' ? t.tr : t.en}
          </button>
        ))}
      </div>

      {/* SMS Kalıpları özel tab */}
      {active === 'sms' ? (
        <SmsKaliplariTab />
      ) : (
        <>
          <div className="card-clay p-4 mb-4">
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-[10px] font-bold text-[var(--text-muted)] mb-1 block">{tr('Yeni kayıt', 'New entry')}</label>
                <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addItem()}
                  placeholder={tr('Ad girin...', 'Enter name...')}
                  className="w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none" data-testid="def-new-input" />
              </div>
              {active === 'belge_turu' && (
                <label className="flex items-center gap-2 text-sm font-semibold py-2 cursor-pointer">
                  <input type="checkbox" checked={newMeb} onChange={(e) => setNewMeb(e.target.checked)} className="rounded" />
                  {tr('MEB kritik', 'MEB critical')}
                </label>
              )}
              <button onClick={addItem} disabled={saving || !newLabel.trim()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-bold border-2 border-green-600 hover:bg-green-600 disabled:opacity-50" data-testid="def-add-btn">
                <Plus size={16} /> {tr('Ekle', 'Add')}
              </button>
            </div>
          </div>

          <div className="card-clay overflow-hidden">
            {loading && <div className="p-4 text-center text-sm text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</div>}
            {!loading && items.length === 0 && <div className="p-6 text-center text-sm text-[var(--text-muted)]">{tr('Kayıt yok. Yukarıdan ekleyin.', 'No entries. Add above.')}</div>}
            {!loading && items.map((it) => (
              <div key={it.def_id} className="flex items-center gap-3 px-4 py-2.5 border-t border-[var(--border-light)] first:border-t-0" data-testid={`def-row-${it.def_id}`}>
                {editId === it.def_id ? (
                  <>
                    <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && saveEdit(it.def_id)}
                      className="flex-1 px-3 py-1.5 rounded-lg border-2 border-[var(--brand-primary)] text-sm font-semibold focus:outline-none" autoFocus />
                    <button onClick={() => saveEdit(it.def_id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Check size={16} /></button>
                    <button onClick={() => setEditId(null)} className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"><X size={16} /></button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-semibold">{it.label_tr}</span>
                    {active === 'belge_turu' && (
                      <button onClick={() => toggleMeb(it)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${it.meb_kritik ? 'bg-red-50 text-red-600 border-red-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                        {it.meb_kritik ? tr('MEB kritik', 'MEB critical') : tr('opsiyonel', 'optional')}
                      </button>
                    )}
                    <button onClick={() => { setEditId(it.def_id); setEditLabel(it.label_tr); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit3 size={15} /></button>
                    <button onClick={() => setDeleteDefTarget(it.def_id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={15} /></button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Silme onayı */}
          <ConfirmDialog
            open={!!deleteDefTarget}
            onOpenChange={(v) => { if (!v && !deletingDef) setDeleteDefTarget(null); }}
            title={tr('Tanımı Sil', 'Delete Definition')}
            description={tr('Bu tanım kalıcı olarak silinecek.', 'This definition will be permanently deleted.')}
            variant="danger"
            confirmLabel={tr('Sil', 'Delete')}
            onConfirm={handleDeleteDef}
            loading={deletingDef}
          />
        </>
      )}
    </AdminLayout>
  );
}
